import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent, Typography } from "@mui/material"
import { ArchiveOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useCallback } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { useExportGlTransactions } from "../hooks/useExportGlTransactions"

import { Button } from "@/components/common/Button"
import { FormDatePicker } from "@/components/UiKit/FormControls/FormDatePicker"

const exportFormSchema = z
  .object({
    from: z.preprocess((val) => val ?? "", z.string().min(1, "From date is required")),
    to: z.preprocess((val) => val ?? "", z.string().min(1, "To date is required")),
  })
  .refine((data) => !data.from || !data.to || data.from <= data.to, {
    message: "From date must be before or equal to To date",
    path: ["to"],
  })

type ExportFormValues = z.infer<typeof exportFormSchema>

interface ExportGlTransactionsDialogProps {
  onClose: () => void
}

export const ExportGlTransactionsDialog: FC<ExportGlTransactionsDialogProps> = ({ onClose }) => {
  const { exportTransactions, isExporting } = useExportGlTransactions()

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<ExportFormValues>({
    resolver: zodResolver(exportFormSchema),
    defaultValues: { from: "", to: "" },
    mode: "onChange",
  })

  const canSubmit = isValid && !Object.keys(errors).length && !isExporting

  const handleFormSubmit = useCallback(
    async ({ from, to }: ExportFormValues) => {
      const success = await exportTransactions(from, to)
      if (success) onClose()
    },
    [exportTransactions, onClose]
  )

  return (
    <Dialog open onClose={onClose} maxWidth='xs' fullWidth data-testid='transactions.export-dialog'>
      <DialogContent className='flex flex-col items-center gap-4 px-8 pb-4 pt-8'>
        <ArchiveOutlined className='text-primary' sx={{ fontSize: 48 }} />

        <Box className='flex flex-col items-center gap-1 text-center'>
          <Typography variant='h5' className='font-normal text-primary'>
            Export Posted and Reversed Transactions
          </Typography>
          <Typography variant='body2' className='text-gray-600'>
            Select a date range of data to export
          </Typography>
        </Box>

        <Box className='flex w-full flex-col gap-4 pt-2'>
          <FormDatePicker
            control={control}
            name='from'
            label='From'
            required
            data-testid='transactions.export-dialog.from-date'
          />
          <FormDatePicker
            control={control}
            name='to'
            label='To'
            required
            data-testid='transactions.export-dialog.to-date'
          />
        </Box>
      </DialogContent>

      <DialogActions className='flex justify-between px-8 pb-8 pt-2'>
        <Button appearance='outlined' onClick={onClose} data-testid='transactions.export-dialog.cancel-button'>
          Cancel
        </Button>
        <Button
          appearance='contained'
          disabled={!canSubmit}
          loading={isExporting}
          onClick={handleSubmit(handleFormSubmit)}
          data-testid='transactions.export-dialog.export-button'
        >
          Export
        </Button>
      </DialogActions>
    </Dialog>
  )
}
